import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Facebook, Instagram, Linkedin, ArrowRight } from "lucide-react";
import { COMPANY, NAV } from "../mock";
import { SEO_SERVICES, SERVICE_AREAS } from "../seo";

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="bg-brand-navydark text-white/80">
      {/* CTA strip */}
      <div className="bg-gradient-to-r from-brand-ocean to-brand-sky">
        <div className="container-x py-10 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          <div>
            <h3 className="text-2xl md:text-3xl font-heading font-extrabold text-white">
              Ready to transform your outdoor space?
            </h3>
            <p className="text-white/90 mt-1">Fast, free, no-obligation estimates across the Space Coast.</p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/estimate"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-lg bg-brand-gold hover:bg-brand-gold2 text-brand-navy font-heading font-bold shadow-gold transition-colors"
            >
              Get My Free Quote <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href={COMPANY.phoneHref}
              data-call-source="footer-cta"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-lg bg-brand-navy/30 hover:bg-brand-navy/50 text-white font-heading font-bold ring-1 ring-white/30 transition-colors"
            >
              <Phone className="h-4 w-4" /> {COMPANY.phone}
            </a>
          </div>
        </div>
      </div>

      <div className="container-x py-8 md:py-14 grid grid-cols-2 gap-x-6 gap-y-8 md:gap-10 md:grid-cols-2 lg:grid-cols-5">
        <div className="col-span-2 md:col-span-1 lg:col-span-1">
          <img src={COMPANY.logo} alt={`${COMPANY.name} logo`} className="h-14 md:h-16 logo-glow" />
          <p className="mt-3 md:mt-4 text-sm leading-relaxed max-w-xs">
            The premium screen & pool enclosure specialists in Melbourne, FL. Family-owned, licensed &
            insured - building luxurious backyard retreats built to last.
          </p>
          <div className="flex items-center gap-3 mt-5">
            <a href={COMPANY.social.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="h-10 w-10 grid place-items-center rounded-full bg-white/10 hover:bg-brand-sky transition-colors">
              <Facebook className="h-4 w-4" />
            </a>
            <a href={COMPANY.social.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="h-10 w-10 grid place-items-center rounded-full bg-white/10 hover:bg-brand-sky transition-colors">
              <Instagram className="h-4 w-4" />
            </a>
            <a href={COMPANY.social.googleMaps} target="_blank" rel="noopener noreferrer" aria-label="Find us on Google Maps" data-testid="footer-google-maps" className="h-10 w-10 grid place-items-center rounded-full bg-white/10 hover:bg-brand-sky transition-colors">
              <MapPin className="h-4 w-4" />
            </a>
          </div>
        </div>

        <div>
          <h4 className="text-white font-heading font-bold text-base md:text-lg mb-3 md:mb-4">Quick Links</h4>
          <ul className="space-y-2 md:space-y-2.5 text-sm">
            {NAV.map((n) => (
              <li key={n.to}>
                <Link to={n.to} className="hover:text-brand-gold2 transition-colors">{n.label}</Link>
              </li>
            ))}
            <li><Link to="/estimate" className="hover:text-brand-gold2 transition-colors">Free Estimate</Link></li>
            <li><Link to="/accessibility-statement" className="hover:text-brand-gold2 transition-colors">Accessibility Statement</Link></li>
            <li><Link to="/privacy-policy" className="hover:text-brand-gold2 transition-colors">Privacy Policy</Link></li>
            <li><Link to="/terms-conditions" className="hover:text-brand-gold2 transition-colors">Terms &amp; Conditions</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-white font-heading font-bold text-base md:text-lg mb-3 md:mb-4">Our Services</h4>
          <ul className="space-y-2 md:space-y-2.5 text-sm">
            {SEO_SERVICES.map((s) => (
              <li key={s.slug}>
                <Link to={s.path} className="hover:text-brand-gold2 transition-colors">{s.nav}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="col-span-2 md:col-span-1">
          <h4 className="text-white font-heading font-bold text-base md:text-lg mb-3 md:mb-4">Service Areas</h4>
          <ul className="space-y-2 md:space-y-2.5 text-sm columns-2 gap-4">
            {SERVICE_AREAS.map((c) => (
              <li key={c.slug}>
                <Link to={`/service-areas/${c.slug}`} className="hover:text-brand-gold2 transition-colors">{c.name}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="col-span-2 md:col-span-1">
          <h4 className="text-white font-heading font-bold text-base md:text-lg mb-3 md:mb-4">Get In Touch</h4>
          <ul className="space-y-3 md:space-y-4 text-sm">
            <li className="flex items-start gap-3">
              <Phone className="h-5 w-5 text-brand-sky2 shrink-0 mt-0.5" />
              <a href={COMPANY.phoneHref} data-call-source="footer" className="hover:text-brand-gold2 transition-colors">{COMPANY.phone}</a>
            </li>
            <li className="flex items-start gap-3">
              <Mail className="h-5 w-5 text-brand-sky2 shrink-0 mt-0.5" />
              <a href={`mailto:${COMPANY.email}`} className="hover:text-brand-gold2 transition-colors break-all">{COMPANY.email}</a>
            </li>
            <li className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-brand-sky2 shrink-0 mt-0.5" />
              <span>{COMPANY.hours}</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-x py-6 pb-24 lg:pb-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/60 text-center sm:text-left">
          <p data-testid="footer-copyright">© {year} {COMPANY.name}. All rights reserved. Licensed & Insured. License Number: SCC131153957</p>
          <p>
            Website Designed By{" "}
            <a
              href="https://bp.agency"
              target="_blank"
              rel="noopener noreferrer"
              className="font-bold text-white hover:text-brand-gold2 transition-colors"
            >
              Brand Partners
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
